// Outbox：所有本地变更追加到这里，随后由 engine 批量 push。
// 使用 localStorage 持久化，保证离线写入不丢失。

import type { PushOp } from '@doneline/sync';
import { isGuestActive, recordGuestTombstone } from '../auth/guest-marker';

const KEY = 'doneline-outbox-v1';
const SINCE_KEY = 'doneline-sync-since-v1';
const SYNC_USER_KEY = 'doneline-sync-user-v1';
const PENDING_TASK_TAGS_KEY = 'doneline-sync-pending-task-tags-v1';

export interface PendingTaskTag {
  taskId: string;
  tagId: string;
  deletedAt: string | null;
}

type Listener = () => void;

// 实体键：entity_kind 是固定枚举（不含 ':'），第一个 ':' 即分界，拼接无歧义
// （task_tag 的 entity_id 本身带 ':' 也不冲突）。
function entityKey(op: PushOp): string {
  return `${op.entity_kind}:${op.entity_id}`;
}

export class OutboxStore {
  private ops: PushOp[] = [];
  private listeners = new Set<Listener>();
  // 最近一次 save() 是否失败（QuotaExceeded / 隐私模式禁写等）。失败时内存是唯一权威：
  // load() 不得再用存储内容整包替换内存副本（否则下一次 enqueue 的 RMW load 会把
  // 没能落盘的 op 静默抹掉 = 「导入成功」实际丢数据）。save 成功即清。
  private persistFailed = false;

  constructor() {
    this.load();
    // 桌面壳有两个同源 webview（main + quickbar）共享同一份 localStorage，但各有一份内存
    // outbox 单例。没有这个监听时：quickbar enqueue 写了 localStorage，main 的内存副本仍是旧的，
    // main 下次 save() 会用旧副本整包覆盖、把 quickbar 刚入队的 op 静默丢掉（换设备即丢任务）。
    // 监听跨窗口写入 → reload + 通知 listeners。web/PWA 单窗口下 storage 事件不在本窗口触发，no-op。
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      window.addEventListener('storage', (e) => {
        if (e.key === KEY) {
          this.load();
          for (const l of this.listeners) l();
          // 兜底投递：另一窗口（quickbar）入队了 op。quickbar 隐藏后其自身的 debounced push
          // 会被 WKWebView 节流、可能迟迟不上行；本窗口（main，sync engine 常驻）收到 storage
          // 事件后主动起一次 push，保证 op 上行（否则用户在 quickbar 加完任务不回主窗口就漏同步）。
          // 动态 import 断开与 engine 的循环依赖；schedulePush 内部 debounce + hasApiConfig/空批短路。
          // 单窗口 web/PWA 下 storage 事件不在本窗口触发，天然 no-op。
          if (this.ops.length > 0) {
            void import('./engine').then((m) => m.schedulePush()).catch(() => {});
          }
        }
      });
    }
  }

  private load() {
    let stored: PushOp[] = [];
    try {
      const raw = localStorage.getItem(KEY);
      stored = raw ? (JSON.parse(raw) as PushOp[]) : [];
    } catch {
      stored = [];
    }
    if (this.persistFailed) {
      // 上次 save 失败：存储内容落后于内存（缺没落盘的 op）。整包替换会把它们抹掉，
      // 改为按 op_id 并集合并（与 mobile hydrate 同款）：存储行在前（含另一窗口可能
      // 写入的 op），内存独有的追加在后（保持入队时序）。跨窗口 storage 事件 reload
      // 走的也是这里，同样并集。
      const storedIds = new Set(stored.map((o) => o.op_id));
      const memoryOnly = this.ops.filter((o) => !storedIds.has(o.op_id));
      this.ops = [...stored, ...memoryOnly];
      return;
    }
    this.ops = stored;
  }

  private save() {
    try {
      localStorage.setItem(KEY, JSON.stringify(this.ops));
      this.persistFailed = false;
    } catch (err) {
      // 不吞：标记失败（importPayload 据此把「假成功」变成显式错误提示），内存 ops 保留，
      // 之后任一次 save（如推送成功后的 removeByOpIds 缩小体积）都会整包重试。
      // 直接 console.error 而非 logger，避免循环依赖（logger 顶层用了 outbox? 否则可改）
      this.persistFailed = true;
      console.error('[outbox] save failed', err);
    }
    for (const l of this.listeners) l();
  }

  /** 最近一次落盘是否失败（true = 内存里有没能持久化的 op，刷新即丢）。 */
  hasPersistFailure(): boolean {
    return this.persistFailed;
  }

  enqueue(op: PushOp) {
    // guest 短路（设计 v4 §3.1）：guest 期不写 outbox —— 登录采纳时由快照整体重建，
    // op 序列历史无意义；不短路则 outbox 无界增长 + suspicious leg2 误 wipe + 401 push。
    // 短路必须在**类方法层**（store 有 ~10 条绕过 helper 直调的路径）。delete 记入
    // guestTombstones 作跨窗口删除证据（否则桌面双 webview 下删除会被并集复活）。
    if (isGuestActive()) {
      if (op.op_kind === 'delete') recordGuestTombstone(op.entity_kind, op.entity_id);
      return;
    }
    // read-modify-write：先 load 合并另一窗口可能已写入的 op（跨 webview 共享 localStorage），
    // 否则用陈旧内存副本 save 会覆盖掉对方的入队（数据丢失）。op_id 去重保证不会重复推。
    this.load();
    this.applyOp(op, this.findLastEntityIdx(op));
    this.save();
  }

  // 批量入队：与逐条 enqueue 完全等价（同一 subset-merge 规则、同一顺序、同一跨窗口 RMW 语义），
  // 但整批只做一次 load + 一次 save。逐条路径每个 op 都 load()（全量 JSON.parse）+ 倒扫 +
  // save()（全量 stringify），Settings 导入几千行时是 O(N²) 的同步 localStorage 写，
  // 主线程会冻结数秒 —— 批量路径把它降到 O(N)。
  enqueueMany(ops: PushOp[], opts?: { ignoreGuestGate?: boolean }) {
    if (ops.length === 0) return;
    // ignoreGuestGate：engine 采纳块专用 —— 采纳 enqueue 发生在 startSync 内，
    // 不依赖 session 模块 emit 时序也能确定入队（其余调用一律走 guest 短路）。
    if (!opts?.ignoreGuestGate && isGuestActive()) {
      for (const op of ops) {
        if (op.op_kind === 'delete') recordGuestTombstone(op.entity_kind, op.entity_id);
      }
      return;
    }
    this.load();
    // 一次性建「实体 → 最后槽位」索引，替代逐 op 全量倒扫（否则内存 pass 本身仍是 O(N²)）。
    // 索引语义与 findLastEntityIdx 一致：每个实体记录其最后一个 op 的下标。
    const lastIdxByEntity = new Map<string, number>();
    for (let i = 0; i < this.ops.length; i++) {
      lastIdxByEntity.set(entityKey(this.ops[i]!), i);
    }
    for (const op of ops) {
      const key = entityKey(op);
      lastIdxByEntity.set(key, this.applyOp(op, lastIdxByEntity.get(key) ?? -1));
    }
    this.save();
  }

  // 找同实体的「最后一个」op 槽位（单条 enqueue 用；批量路径用预建索引替代倒扫）。
  private findLastEntityIdx(op: PushOp): number {
    for (let i = this.ops.length - 1; i >= 0; i--) {
      const o = this.ops[i]!;
      if (o.entity_kind === op.entity_kind && o.entity_id === op.entity_id) return i;
    }
    return -1;
  }

  // 合并/追加单个 op（不 load 不 save —— RMW 边界由调用方负责）。返回实体最终所在槽位下标。
  //
  // 合并同一实体的连续 upsert，但只能并入该实体的「最后一个」op 且它本身是 upsert。
  // 不能并入最早的 upsert 槽位：若中间夹了 delete（upsert→delete→upsert），并回最早槽位
  // 会得到 [upsert', delete] —— push 按序执行后实体最终被删，丢掉用户最后一次 upsert。
  //
  // 合并的前置条件：旧槽位的字段集 ⊆ 新 op 的字段集。否则旧 op 里"本次未再改动"的字段
  // 会被整体盖上新 client_ts —— 服务端 per-field LWW 会让这些旧值以"更新的时钟"打赢另一
  // 设备真正更新的编辑（lost update）。子集时 merged 恒等于新 op（值与时钟都真实来自本次
  // 动作）；非子集则各自独立成 op，push 按序应用，per-field 时钟保持真实。
  private applyOp(op: PushOp, lastIdx: number): number {
    const slot = lastIdx >= 0 ? this.ops[lastIdx]! : null;
    const canMerge =
      op.op_kind === 'upsert' &&
      slot !== null &&
      slot.op_kind === 'upsert' &&
      Object.keys(slot.fields).every((k) => k in op.fields);
    if (canMerge) {
      this.ops[lastIdx] = {
        ...slot!,
        fields: { ...op.fields },
        op_id: op.op_id,
        client_ts: op.client_ts,
      };
      return lastIdx;
    }
    this.ops.push(op);
    return this.ops.length - 1;
  }

  /**
   * 跨窗口快照合并（lib/cross-window-merge.ts）的证据：待推 upsert / delete 的
   * entity_id 集。task_tag 除外——复合 id 不对应快照实体行。同实体多 op 按入队序
   * last-wins（upsert→delete 归删除集；理论上的 delete→upsert 归新增集）。
   * 先 RMW load，把另一窗口刚写入的 op 也纳入证据。
   */
  pendingEntityEvidence(): { pendingUpsertIds: Set<string>; pendingDeleteIds: Set<string> } {
    this.load();
    const pendingUpsertIds = new Set<string>();
    const pendingDeleteIds = new Set<string>();
    for (const o of this.ops) {
      if (o.entity_kind === 'task_tag') continue;
      if (o.op_kind === 'delete') {
        pendingDeleteIds.add(o.entity_id);
        pendingUpsertIds.delete(o.entity_id);
      } else {
        pendingUpsertIds.add(o.entity_id);
        pendingDeleteIds.delete(o.entity_id);
      }
    }
    return { pendingUpsertIds, pendingDeleteIds };
  }

  peek(limit = 200): PushOp[] {
    // load 先同步另一窗口（quickbar）入队的 op，保证 engine push 批次包含它们（其自身的 debounced
    // push 在隐藏 webview 里可能被挂起，靠 main 的 push 兜底）。单窗口下 load 读到的就是自身刚写的。
    this.load();
    return this.ops.slice(0, limit);
  }

  removeByOpIds(opIds: string[]) {
    if (opIds.length === 0) return;
    // read-modify-write：先 load，避免把另一窗口此刻新入队的 op 一起抹掉。
    this.load();
    const set = new Set(opIds);
    this.ops = this.ops.filter((o) => !set.has(o.op_id));
    this.save();
  }

  size(): number {
    return this.ops.length;
  }

  clear() {
    this.ops = [];
    this.save();
  }

  subscribe(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }
}

export const outbox = new OutboxStore();

// ---------- sinceVersion 游标 ----------

export function getSinceVersion(): number {
  try {
    const raw = localStorage.getItem(SINCE_KEY);
    return raw ? Number(raw) : 0;
  } catch {
    return 0;
  }
}

export function setSinceVersion(v: number) {
  try {
    localStorage.setItem(SINCE_KEY, String(v));
  } catch {
    // ignore
  }
}

export function clearSinceVersion() {
  try {
    localStorage.removeItem(SINCE_KEY);
  } catch {
    // ignore
  }
}

// ---------- last synced user_id ----------
//
// 同一台设备上历史登录过多个账户（mock-mode → 真实 Supabase；或者换 Supabase user）
// 时，sinceVersion 是基于上一个 user 的视图前进的，新 user 用这个 cursor pull 会跳过
// row_version 比 cursor 小的全部历史数据，造成"数据不同步"假象。
//
// 解法：把 cursor 跟 user_id 绑定起来。startSync 检测到 user 变了就 clearSinceVersion，
// 强制 since=0 全量 bootstrap。

export function getLastSyncedUserId(): string | null {
  try {
    return localStorage.getItem(SYNC_USER_KEY);
  } catch {
    return null;
  }
}

export function setLastSyncedUserId(userId: string) {
  try {
    localStorage.setItem(SYNC_USER_KEY, userId);
  } catch {
    // ignore
  }
}

export function clearLastSyncedUserId() {
  try {
    localStorage.removeItem(SYNC_USER_KEY);
  } catch {
    // ignore
  }
}

// ---------- unresolved task_tag rows ----------
//
// pull 分页可能先返回 task_tag、后返回其 task。cursor 每页都会持久化，因此关系缓冲也必须
// 在 cursor 之前可靠落盘；否则两者之间崩溃，重启会从新 cursor 继续，已返回的关系永久不再出现。
let lastPendingTaskTagsPersistOk = true;

function normalizePendingTaskTags(value: unknown): PendingTaskTag[] {
  if (!Array.isArray(value)) return [];
  const byRelation = new Map<string, PendingTaskTag>();
  for (const row of value) {
    if (!row || typeof row !== 'object') continue;
    const x = row as Record<string, unknown>;
    if (typeof x.taskId !== 'string' || typeof x.tagId !== 'string') continue;
    const deletedAt = typeof x.deletedAt === 'string' ? x.deletedAt : null;
    // 同一关系重放时保留最后状态，避免 cursor 未推进后的幂等重拉让缓冲无界增长。
    byRelation.set(`${x.taskId}\u0000${x.tagId}`, {
      taskId: x.taskId,
      tagId: x.tagId,
      deletedAt,
    });
  }
  return [...byRelation.values()];
}

export function getPendingTaskTags(): PendingTaskTag[] {
  try {
    const raw = localStorage.getItem(PENDING_TASK_TAGS_KEY);
    return normalizePendingTaskTags(raw ? JSON.parse(raw) : []);
  } catch {
    return [];
  }
}

export function setPendingTaskTags(rows: PendingTaskTag[]): void {
  try {
    const normalized = normalizePendingTaskTags(rows);
    if (normalized.length === 0) localStorage.removeItem(PENDING_TASK_TAGS_KEY);
    else localStorage.setItem(PENDING_TASK_TAGS_KEY, JSON.stringify(normalized));
    lastPendingTaskTagsPersistOk = true;
  } catch (err) {
    lastPendingTaskTagsPersistOk = false;
    console.error('[sync] pending task_tag save failed', err);
  }
}

export function clearPendingTaskTags(): void {
  setPendingTaskTags([]);
}

export function pendingTaskTagsPersisted(): Promise<boolean> {
  return Promise.resolve(lastPendingTaskTagsPersistOk);
}
